import { REACTION_SETS, type Reaction, type ReactionEvent } from '@xox/shared'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { Sticker } from '../ice/art/stickers'
import { hash, isIceReaction, type LaneMode, laneFor, REACTION_LABELS } from '../lib/reactions'
import { XoxSticker } from './art/xoxStickers'

const COOLDOWN_MS = 600

const GLYPH_SIZE = { bar: 'size-8', float: 'size-14' } as const

/** Renders a reaction id as its game's drawn sticker. */
function ReactionGlyph({ id, size }: { id: Reaction; size: keyof typeof GLYPH_SIZE }) {
  return isIceReaction(id) ? (
    <Sticker id={id} className={GLYPH_SIZE[size]} />
  ) : (
    <XoxSticker id={id} className={GLYPH_SIZE[size]} />
  )
}

/** Game-agnostic: each room passes its reaction set and its store's sender. */
export function ReactionBar({
  onReact,
  set = REACTION_SETS.xox,
}: {
  onReact: (id: Reaction) => void
  set?: readonly Reaction[]
}) {
  const react = onReact
  const [cooling, setCooling] = useState(false)

  const fire = (id: Reaction) => {
    if (cooling) return
    react(id)
    setCooling(true)
    setTimeout(() => setCooling(false), COOLDOWN_MS)
  }

  return (
    <div
      className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-white p-1 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
      role="group"
      aria-label="Send a reaction"
    >
      {set.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => fire(id)}
          disabled={cooling}
          aria-label={REACTION_LABELS[id]}
          title={REACTION_LABELS[id]}
          className="flex size-11 shrink-0 items-center justify-center rounded-full transition-transform hover:bg-slate-100 active:scale-90 disabled:opacity-60 dark:hover:bg-slate-800"
        >
          <ReactionGlyph id={id} size="bar" />
        </button>
      ))}
    </div>
  )
}

/** Floating bubbles over the board; see `laneFor` for how a bubble picks its column. */
export function ReactionOverlay({
  reactions,
  lanes = 'role',
}: {
  reactions: ReactionEvent[]
  lanes?: LaneMode
}) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-3xl"
      aria-live="off"
    >
      <AnimatePresence>
        {reactions.map((r) => {
          const jitter = (hash(r.id) % 13) - 6
          const label = r.from.nickname
          return (
            <motion.div
              key={r.id}
              className="absolute flex flex-col items-center"
              style={{
                left: `${laneFor(lanes, r.role, r.from.id) + jitter}%`,
                bottom: '8%',
                x: '-50%',
              }}
              initial={{ opacity: 0, y: 20, scale: 0.6 }}
              animate={{ opacity: 1, y: -140, scale: 1.15 }}
              exit={{ opacity: 0, y: -200, scale: 0.9 }}
              transition={{ duration: 2.4, ease: 'easeOut' }}
            >
              <span className="drop-shadow-md">
                <ReactionGlyph id={r.emoji} size="float" />
              </span>
              <span className="mt-0.5 rounded-full bg-slate-900/80 px-2 py-0.5 text-[10px] font-semibold text-white">
                {r.from.avatar} {label}
              </span>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
